import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState, RootDispatch } from "../../store";
import {
  fetchVendorsThunk,
  fetchRawMaterialsThunk,
  deleteWarehouseVendorRawmaterialItemThunk,
  fetchWarehouseNamesAndIdsThunk,
  fetchWarehouseMaterialsThunk,
} from "../../store/slices/warehouseSlice";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";

import ProductWarehouseStock from "./ProductWarehouseStock";
import StockTransferTab from "../Warehouse/StockTransfer/StockTransferTab";
import RawMaterialModal from "../Warehouse/Management/RawMaterialModal";
import RawMaterialsSection from "../Warehouse/Management/RawMaterialsSection";
import VendorModal from "../Warehouse/Management/VendorModal";
import VendorsSection from "../Warehouse/Management/VendorsSection";
import AddStoreInwardModal from "../Warehouse/Store/AddStoreInwardModal";
import ProductManagement from "./ProductManagement";
import AdjustStockModal from "./AdjustStockModal";
import { fetchProducts } from "../../store/slices/productSlice";

const ProductPage: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    vendors,
    totalVendorItems,
    totalVendorPages,
    totalRawMaterialItems,
    totalRawMaterialPages,
  } = useSelector((state: RootState) => state.warehouse);

  const activeMainTab =
    (searchParams.get("main") as "inventory" | "transfers") || "inventory";
  const activeSubTab =
    (searchParams.get("sub") as
      | "products"
      | "stock"
      | "vendors"
      | "materials") || "stock";

  const handleTabChange = (main: string, sub: string) => {
    setSearchParams({ main, sub });
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [modalType, setModalType] = useState<
    "vendor" | "material" | "inward" | "adjust" | null
  >(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [vendorPage, setVendorPage] = useState(1);
  const [rawMaterialPage, setRawMaterialPage] = useState(1);
  const itemsPerPage = 10;

  // New Sorting State
  const [rmSort, setRmSort] = useState<{
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }>({});

  const [vendorForm, setVendorForm] = useState({
    name: "",
    gstNumber: "",
    contactNumber: "",
    address: "",
  });

  const [rawMaterialForm, setRawMaterialForm] = useState<any>({
    type: "",
    name: "",
    sku: "",
    hsn: "",
    unit: undefined,
    usageType: "",
    quantity: undefined,
    approxDeliveryDays: 0,
    gst: undefined,
    requireSerialNumber: false,
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    type: "vendor" | "material" | null;
    id: string | null;
    name: string;
  }>({ isOpen: false, type: null, id: null, name: "" });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (activeSubTab === "vendors") setVendorPage(1);
      if (activeSubTab === "materials") setRawMaterialPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, activeSubTab]);

  useEffect(() => {
    const isReload = window.performance
      .getEntriesByType("navigation")
      .map((nav) => (nav as PerformanceNavigationTiming).type)
      .includes("reload");

    if (isReload) {
      dispatch(fetchWarehouseNamesAndIdsThunk());
    }
  }, [dispatch]);

  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearch("");
    setRmSort({}); // Reset sort on tab change
  }, [activeSubTab, activeMainTab]);

  useEffect(() => {
    if (activeMainTab === "inventory") {
      if (activeSubTab === "vendors" || activeSubTab === "stock") {
        dispatch(
          fetchVendorsThunk({
            page: vendorPage,
            limit: itemsPerPage,
            search: activeSubTab === "vendors" ? debouncedSearch : "",
          }),
        );
      }
      if (activeSubTab === "materials" || activeSubTab === "stock") {
        getRawMaterials();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    activeMainTab,
    activeSubTab,
    vendorPage,
    rawMaterialPage,
    debouncedSearch,
    rmSort,
  ]);

  const getRawMaterials = async () => {
    await dispatch(
      fetchRawMaterialsThunk({
        page: rawMaterialPage,
        limit: itemsPerPage,
        search: activeSubTab === "materials" ? debouncedSearch : "",
        sortBy: rmSort.sortBy,
        sortOrder: rmSort.sortOrder,
        from: "warehouse",
      }),
    );
  };
  const handleEdit = (type: "vendor" | "material", item: any) => {
    setModalType(type);
    setEditMode(true);
    setSelectedItem(item);
    if (type === "vendor") {
      setVendorForm({
        name: item.name,
        gstNumber: item.gstNumber,
        contactNumber: item.contactNumber,
        address: item.address,
      });
    } else {
      setRawMaterialForm({
        type: item.type,
        name: item.name,
        sku: item.sku,
        hsn: item.hsn || "",
        unit: item.unit,
        usageType: item.usageType,
        quantity: item.quantity,
        approxDeliveryDays: item.approxDeliveryDays,
        gst: item.gst ?? 0,
        requireSerialNumber: item.requireSerialNumber ?? false,
      });
    }
  };

  const handleModalOpen = (
    type: "vendor" | "material" | "inward" | "adjust",
  ) => {
    setModalType(type);
    setEditMode(false);
    setSelectedItem(null);
    if (type === "vendor") {
      setVendorForm({
        name: "",
        gstNumber: "",
        contactNumber: "",
        address: "",
      });
    } else if (type === "material") {
      setRawMaterialForm({
        type: "",
        name: "",
        sku: "",
        hsn: "",
        unit: undefined,
        usageType: "",
        quantity: undefined,
        approxDeliveryDays: 0,
        gst: undefined,
        requireSerialNumber: false,
      });
    }
  };

  const handleModalClose = () => {
    setModalType(null);
    setEditMode(false);
    setSelectedItem(null);
  };

  const handleDelete = (type: any, id: string, name: string) => {
    setDeleteConfirmation({
      isOpen: true,
      type: type === "factory" ? null : type,
      id,
      name,
    });
  };

  useEffect(() => {
    dispatch(fetchDropdownData("factory"));
    //TODO: Remove the empty string argument once the API is updated to handle optional query params correctly
    dispatch(fetchWarehouseMaterialsThunk("factory"));
    dispatch(fetchProducts());
    dispatch(fetchWarehouseNamesAndIdsThunk());
  }, [dispatch]);

  const confirmDelete = () => {
    if (deleteConfirmation.type && deleteConfirmation.id) {
      dispatch(
        deleteWarehouseVendorRawmaterialItemThunk({
          id: deleteConfirmation.id,
          type: deleteConfirmation.type as "vendor" | "material",
        }),
      );
      setDeleteConfirmation({ isOpen: false, type: null, id: null, name: "" });
    }
  };

  return (
    <div className="w-full px-6 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Warehouse Operations
        </h1>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleModalOpen("adjust")}
            className="bg-[#0077C0] text-white px-4 py-2 rounded-md font-medium hover:bg-[#005B90] transition-colors shadow-sm"
          >
            Adjust Stock
          </button>
          <button
            onClick={() => handleModalOpen("inward")}
            className="bg-[#0077C0] text-white px-4 py-2 rounded-md font-medium hover:bg-[#005B90]"
          >
            + Store Inward
          </button>
          <button
            onClick={() => handleModalOpen("vendor")}
            className="bg-[#0077C0] text-white px-4 py-2 rounded-md font-medium hover:bg-[#005B90]"
          >
            + Add Vendor
          </button>
          <button
            onClick={() => handleModalOpen("material")}
            className="bg-[#0077C0] text-white px-4 py-2 rounded-md font-medium hover:bg-[#005B90]"
          >
            + Add Material
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange("inventory", activeSubTab)}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeMainTab === "inventory" ? "border-[#0077C0] text-[#0077C0]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Inventory Management
          </button>
          <button
            onClick={() => handleTabChange("transfers", activeSubTab)}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeMainTab === "transfers" ? "border-[#0077C0] text-[#0077C0]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            Stock Transfers
          </button>
        </nav>
      </div>

      {activeMainTab === "inventory" && (
        <div className="mb-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <nav className="flex space-x-6">
            <button
              onClick={() => handleTabChange("inventory", "products")}
              className={`py-3 text-sm font-medium ${activeSubTab === "products" ? "text-[#0077C0] border-b-2 border-[#0077C0]" : "text-gray-500"}`}
            >
              Products
            </button>
            <button
              onClick={() => handleTabChange("inventory", "stock")}
              className={`py-3 text-sm font-medium ${activeSubTab === "stock" ? "text-[#0077C0] border-b-2 border-[#0077C0]" : "text-gray-500"}`}
            >
              Warehouse Stock
            </button>
            <button
              onClick={() => handleTabChange("inventory", "vendors")}
              className={`py-3 text-sm font-medium flex items-center ${activeSubTab === "vendors" ? "text-[#0077C0] border-b-2 border-[#0077C0]" : "text-gray-500"}`}
            >
              Vendors{" "}
              <span className="ml-2 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-semibold">
                {totalVendorItems}
              </span>
            </button>
            <button
              onClick={() => handleTabChange("inventory", "materials")}
              className={`py-3 text-sm font-medium flex items-center ${activeSubTab === "materials" ? "text-[#0077C0] border-b-2 border-[#0077C0]" : "text-gray-500"}`}
            >
              Raw Materials{" "}
              <span className="ml-2 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-semibold">
                {totalRawMaterialItems}
              </span>
            </button>
          </nav>

          {(activeSubTab === "vendors" || activeSubTab === "materials") && (
            <div className="relative rounded-md shadow-sm w-full sm:w-64 mb-2 sm:mb-0">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500">🔍</span>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0077C0] focus:border-transparent outline-none transition-all"
                placeholder={`Search ${activeSubTab}...`}
              />
            </div>
          )}
        </div>
      )}

      <div className="w-full">
        {activeMainTab === "inventory" ? (
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            {activeSubTab === "products" && <ProductManagement />}
            {activeSubTab === "stock" && <ProductWarehouseStock />}
            {activeSubTab === "vendors" && (
              <VendorsSection
                vendors={vendors}
                vendorPage={vendorPage}
                totalVendorPages={totalVendorPages}
                totalVendorItems={totalVendorItems}
                itemsPerPage={itemsPerPage}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleVendorPageChange={setVendorPage}
              />
            )}
            {activeSubTab === "materials" && (
              <RawMaterialsSection
                getRawMaterials={getRawMaterials}
                rawMaterialPage={rawMaterialPage}
                totalRawMaterialPages={totalRawMaterialPages}
                totalRawMaterialItems={totalRawMaterialItems}
                itemsPerPage={itemsPerPage}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleRawMaterialPageChange={setRawMaterialPage}
                onSort={(sortBy, sortOrder) => setRmSort({ sortBy, sortOrder })}
              />
            )}
          </div>
        ) : (
          <StockTransferTab />
        )}
      </div>

      <VendorModal
        isOpen={modalType === "vendor"}
        onClose={handleModalClose}
        editMode={editMode}
        vendorForm={vendorForm}
        setVendorForm={setVendorForm as any}
        selectedItem={selectedItem}
        vendorPage={vendorPage}
        itemsPerPage={itemsPerPage}
        searchQuery={debouncedSearch}
      />
      <RawMaterialModal
        isOpen={modalType === "material"}
        onClose={handleModalClose}
        editMode={editMode}
        rawMaterialForm={rawMaterialForm}
        setRawMaterialForm={setRawMaterialForm as any}
        selectedItem={selectedItem}
        rawMaterialPage={rawMaterialPage}
        itemsPerPage={itemsPerPage}
        searchQuery={debouncedSearch}
        getRawMaterials={getRawMaterials}
      />
      <AddStoreInwardModal
        isOpen={modalType === "inward"}
        onClose={handleModalClose}
        from="warehouse"
      />

      <AdjustStockModal
        isOpen={modalType === "adjust"}
        onClose={handleModalClose}
      />

      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              Confirm Deletion
            </h3>
            <p className="text-gray-600 mb-6 italic">
              Are you sure you want to delete <b>{deleteConfirmation.name}</b>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() =>
                  setDeleteConfirmation({
                    ...deleteConfirmation,
                    isOpen: false,
                  })
                }
                className="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;
